import { CommonModule, Location } from '@angular/common';
import { Component, ElementRef, HostListener, Input, OnInit, Renderer2, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { OverlayPanel, OverlayPanelModule } from 'primeng/overlaypanel';
import { combineLatest, Subject, takeUntil } from 'rxjs';
import { EcommerceCategory, User } from '../../models';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { ErrorReportingService } from '../../services/error-reporting/error-reporting.service';
import { FavoritesService } from '../../services/favorites/favorites.service';
import { PlatformService } from '../../services/platform/platform.service';
import { RemoteConfigsService } from '../../services/remote-configs/remote-configs.service';
import { RealEstateLocationSuggestion } from '../../services/real-estate-locations/real-estate-locations.service';
import { SearchHistoryService } from '../../services/search-history/search-history.service';
import { SessionsService } from '../../services/sessions/sessions.service';
import { ShoppingCartDetail, ShoppingCartsService } from '../../services/shopping-carts/shopping-carts.service';
import { UsersService } from '../../services/users/users.service';
import { mapShoppingCartItemProductsToCartUpdateItems } from '../../utils/analytics.utils';
import { getActiveEcommerceCategories } from '../../utils/ecommerce.utils';
import { ButtonComponent } from '../button/button.component';
import { EcommerceFilterSidebarComponent, FilterSidebarValues } from '../ecommerce-filter-sidebar/ecommerce-filter-sidebar.component';
import { EcommerceSearchInputComponent } from '../ecommerce-search-input/ecommerce-search-input.component';
import { EcommerceSearchSidebarComponent } from '../ecommerce-search-sidebar/ecommerce-search-sidebar.component';
import { MenuSidebarComponent } from '../menu-sidebar/menu-sidebar.component';
import { RealEstateFilterSidebarComponent, RealEstateFilterSidebarValues } from '../real-estate-filter-sidebar/real-estate-filter-sidebar.component';
import { RealEstateSearchInputComponent } from '../real-estate-search-input/real-estate-search-input.component';
import { RealEstateSearchSidebarComponent } from '../real-estate-search-sidebar/real-estate-search-sidebar.component';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [
    ButtonComponent,
    CommonModule,
    EcommerceFilterSidebarComponent,
    EcommerceSearchInputComponent,
    EcommerceSearchSidebarComponent,
    MenuSidebarComponent,
    OverlayPanelModule,
    RealEstateFilterSidebarComponent,
    RealEstateSearchInputComponent,
    RealEstateSearchSidebarComponent,
    RouterModule,
  ],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class NavbarComponent implements OnInit {
  @Input() showSearchIcon = false;
  @ViewChild('ecommerceFilterSidebar') private ecommerceFilterSidebar: EcommerceFilterSidebarComponent;
  @ViewChild('ecommerceSearchSidebar') private ecommerceSearchSidebar: EcommerceSearchSidebarComponent;
  @ViewChild('realEstateFilterSidebar') private realEstateFilterSidebar: RealEstateFilterSidebarComponent;
  @ViewChild('realEstateSearchSidebar') private realEstateSearchSidebar: RealEstateSearchSidebarComponent;
  @ViewChild('overlayPanel') private overlayPanel: OverlayPanel;
  ecommerceCategories: EcommerceCategory[] = [];
  ecommerceFilters: string | null = null;
  ecommerceOrder: string | null = null;
  ecommerceQuery: string | null = null;
  ecommerceSearchHistory: string[] | null = null;
  firstLoadFinished = false;
  hasActiveEcommerceFilters = false;
  hasActiveRealEstateFilters = false;
  hasFavorites = false;
  isEcommerceProductDetail = false;
  isEcommerceProductList = false;
  isEcommerce = false;
  isEnrollment = false;
  isHome = true;
  isPrivateSite = false;
  isRealEstate = false;
  isRealEstateSearch = false;
  isShoppingCartInitialized = false;
  menuSidebarVisible = false;
  realEstateFilterValues: RealEstateFilterSidebarValues = { filters: null, order: null };
  realEstateQuery: string | null = null;
  shoppingCartDetail: ShoppingCartDetail | null = null;
  user: User | null = null;
  userNameInitials = '';
  protected ecommerceCategory: string | null = null;
  private isScrolled = false;
  private isMobile = false;
  private isTablet = false;
  private prevScrollPos = 0;
  private pageUrl: string;
  private viewDestroyed = new Subject<void>();

  constructor(
    private activatedRoute: ActivatedRoute,
    private analyticsService: AnalyticsService,
    private elementRef: ElementRef,
    private errorReportingService: ErrorReportingService,
    private favoritesService: FavoritesService,
    private location: Location,
    private platformService: PlatformService,
    private remoteConfigsService: RemoteConfigsService,
    private renderer: Renderer2,
    private router: Router,
    private searchHistoryService: SearchHistoryService,
    private sessionsService: SessionsService,
    private shoppingCartsService: ShoppingCartsService,
    private usersService: UsersService,
  ) {}

  ngOnInit(): void {
    if (this.platformService.isServer()) {
      return;
    }
    combineLatest([
      this.router.events,
      this.activatedRoute.queryParams,
    ]).pipe(takeUntil(this.viewDestroyed)).subscribe({
      next: ([event, queryParams]) => {
        this.pageUrl = this.router.url.split('?')[0];
        this.ecommerceCategory = this.pageUrl.startsWith('/ecommerce/c/') ? (this.pageUrl.split('/')[3] ?? null) : null;
        if (event instanceof NavigationEnd) {
          const isEcommerceProductList = event.url.startsWith('/ecommerce/search') || event.url.startsWith('/ecommerce/c');
          const newLoadEcommerceSearch = !this.isEcommerceProductList && isEcommerceProductList;
          this.isHome = event.url === '/';
          this.isEcommerce =  event.url.startsWith('/ecommerce');
          this.isEcommerceProductList = isEcommerceProductList;
          this.isEcommerceProductDetail = event.url.startsWith('/ecommerce/products');
          this.isEnrollment = event.url.startsWith('/enrollment');
          this.isPrivateSite = event.url.startsWith('/private-site');
          this.isRealEstate = event.url.startsWith('/desarrolladores');
          this.isRealEstateSearch = event.url.startsWith('/desarrolladores/buscar');
          if (newLoadEcommerceSearch) {
            window.scrollTo(0, 0);
          }
        }
        const query = queryParams['query'] ?? null;
        const filters = queryParams['filters'] ?? null;
        const order = queryParams['order'] ?? null;
        if (this.pageUrl.startsWith('/ecommerce')) {
          this.ecommerceQuery = query;
          this.ecommerceFilters = filters;
          this.ecommerceOrder = order;
        }
        if (this.pageUrl.startsWith('/desarrolladores/buscar')) {
          const realEstateFilters = filters ? JSON.parse(filters) as RealEstateFilterSidebarValues['filters'] : null;
          const settlement = realEstateFilters?.find((filter) => filter.field === 'settlements')?.value;
          const municipality = realEstateFilters?.find((filter) => filter.field === 'municipality')?.value;
          const state = realEstateFilters?.find((filter) => filter.field === 'state')?.value;
          const settlements = (Array.isArray(settlement) ? settlement : [settlement])
            .filter((value): value is string => typeof value === 'string' && !!value.trim());
          const location = [
            ...settlements,
            municipality,
            state,
          ].filter((value): value is string => typeof value === 'string' && !!value.trim()).join(', ');
          this.realEstateQuery = settlements.length > 1 ? null : location || query;
          this.realEstateFilterValues = {
            filters: realEstateFilters,
            order: order ? JSON.parse(order) as RealEstateFilterSidebarValues['order'] : null,
          };
        }
      },
    });
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.initialize();
    this.isTablet = window.innerWidth >= 1000 && window.innerWidth < 1366;
    this.isMobile = window.innerWidth < 1000;
    this.prevScrollPos = window.scrollY;
  }

  changeEcommerceFilters(hasActiveFilters: boolean): void {
    this.hasActiveEcommerceFilters = hasActiveFilters;
  }

  changeRealEstateFilters(hasActiveFilters: boolean): void {
    this.hasActiveRealEstateFilters = hasActiveFilters;
  }

  async changeEcommerceQuery(query: string | null): Promise<void> {
    this.ecommerceQuery = query;
    if (!this.ecommerceQuery) {
      this.hasActiveEcommerceFilters = false;
      this.ecommerceFilterSidebar.deleteFilters();
    }
  }

  closedMenuSidebar(): void {
    this.menuSidebarVisible = false;
  }

  async deleteEcommerceSearchHistory(): Promise<void> {
    await this.searchHistoryService.removeAll();
  }

  async filterEcommerce(values: FilterSidebarValues): Promise<void> {
    let page = this.pageUrl;
    if (!values.category && !this.pageUrl.startsWith('/ecommerce/search')) {
      page = '/ecommerce/search';
    }
    if (values.category && !this.pageUrl.includes(values.category)) {
      page = `/ecommerce/c/${values.category}`;
    }
    await this.router.navigate([page],
      {
        queryParams: {
          ...(this.ecommerceQuery && { query: this.ecommerceQuery }),
          ...(values.encodedFilters && { filters: values.encodedFilters }),
          ...(values.encodedOrder && { order: values.encodedOrder }),
        },
      });
  }

  async filterRealEstate(values: RealEstateFilterSidebarValues): Promise<void> {
    let page = this.pageUrl;
    if (!this.pageUrl.startsWith('/desarrolladores/buscar')) {
      page = '/desarrolladores/buscar';
    }
    await this.router.navigate([page],
      {
        queryParams: {
          ...(this.realEstateQuery && { query: this.realEstateQuery }),
          ...(values.filters && { filters: JSON.stringify(values.filters) }),
          ...(values.order && { order: JSON.stringify(values.order) }),
        },
      });
  }

  goBack(): void {
    this.location.back();
  }

  goTo(route: string): void {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.router.navigate([route]);
    this.overlayPanel.hide();
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  @HostListener('window:scroll', [])
  onWindowScroll(): void {
    if (this.platformService.isServer()) {
      return;
    }
    const scrollY = window.scrollY;
    const header = this.elementRef.nativeElement.querySelector('.header-navbar');
    const logo = this.elementRef.nativeElement.querySelector('.logo');
    if (this.isHome) {
      if (scrollY > 50 && !this.isScrolled) {
        this.renderer.addClass(header, 'scrolled');
        this.renderer.removeClass(logo, 'large');
        this.renderer.addClass(logo, 'small');
        this.isScrolled = true;
      } else if (scrollY <= 50 && this.isScrolled) {
        this.renderer.removeClass(header, 'scrolled');
        this.renderer.removeClass(logo, 'small');
        this.renderer.addClass(logo, 'large');
        this.isScrolled = false;
      }
      return;
    }
    if (scrollY > 0 && !this.isScrolled) {
      this.renderer.addClass(header, 'scrolled');
      this.renderer.removeClass(logo, 'large');
      this.renderer.addClass(logo, 'small');
      this.isScrolled = true;
    } else if (scrollY === 0) {
      if (this.isScrolled) {
        this.renderer.removeClass(header, 'scrolled');
        this.isScrolled = false;
      } else {
        this.renderer.addClass(logo, 'small');
      }
    }
    if (this.isEcommerce) {
      const mainContainer = this.elementRef.nativeElement.querySelector('.entity-complementary-container');
      this.showSearchIcon = scrollY >= 134;
      this.renderer.setStyle(mainContainer, 'box-shadow', scrollY >= 1 ? 'var(--light-shadow-08)' : 'none');
      if (scrollY >= 134 && !this.isEcommerceProductList && (this.isTablet || this.isMobile)) {
        this.renderer.setStyle(mainContainer, 'top', this.isTablet ? '99px' : '71px');
        this.renderer.setStyle(mainContainer, 'position', 'sticky');
      }
      if (this.isEcommerceProductList) {
        this.renderer.setStyle(mainContainer, 'transform', 'translateY(0)');
        this.renderer.setStyle(mainContainer, 'top', '0');
        if (scrollY < 100) {
          this.renderer.addClass(mainContainer, 'sticky');
          this.renderer.setStyle(this.elementRef.nativeElement.querySelector('.entity-bar-spacer'), 'height', '0');
        }
      } else if (this.prevScrollPos > scrollY) {
        this.renderer.setStyle(mainContainer, 'transform', 'translateY(0)');
      } else if (this.prevScrollPos < scrollY && scrollY > 110) {
        this.renderer.setStyle(mainContainer, 'transform', 'translateY(-100%)');
      }
    }
    this.prevScrollPos = scrollY;
  }

  openEcommerceFilters(): void {
    this.ecommerceFilterSidebar.open();
  }

  openEcommerceSearch(): void {
    this.ecommerceSearchSidebar.open();
  }

  openRealEstateFilters(): void {
    this.realEstateFilterSidebar.open();
  }

  openRealEstateSearch(): void {
    this.realEstateSearchSidebar.open();
  }

  async searchEcommerce(query: string): Promise<void> {
    if (this.ecommerceSearchSidebar.isOpen()) {
      this.ecommerceSearchSidebar.close();
    }
    this.ecommerceQuery = query;
    this.analyticsService.logSearchEvent({ search_term: this.ecommerceQuery });
    this.hasActiveEcommerceFilters = false;
    await this.searchHistoryService.create(query);
    await this.router.navigateByUrl(`/ecommerce/search?query=${this.ecommerceQuery}`);
  }

  async searchRealEstate(suggestion: RealEstateLocationSuggestion): Promise<void> {
    const filters = (this.realEstateFilterValues.filters ?? []).filter((filter) => !['state', 'municipality', 'settlements'].includes(filter.field));
    filters.push({ field: 'state', operator: '=', value: suggestion.state });
    if (suggestion.municipality) {
      filters.push({ field: 'municipality', operator: '=', value: suggestion.municipality });
    }
    if (suggestion.settlement) {
      filters.push({ field: 'settlements', operator: 'in', value: [suggestion.settlement] });
    }
    await this.router.navigate(['/desarrolladores/buscar'], {
      queryParams: {
        query: suggestion.label,
        filters: JSON.stringify(filters),
        ...(this.realEstateFilterValues.order && { order: JSON.stringify(this.realEstateFilterValues.order) }),
      },
    });
  }

  showMenuSidebar(): void {
    this.menuSidebarVisible = true;
  }

  signOut(): void {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.sessionsService.signOut();
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.router.navigate(['/']);
    this.overlayPanel.hide();
  }

  private async initialize(): Promise<void> {
    try {
      const ecommerceCategories = await this.remoteConfigsService.getEcommerceCategories();
      this.ecommerceCategories = getActiveEcommerceCategories(ecommerceCategories);
    } catch (error) {
      this.errorReportingService.log('NavbarComponent.initialize()', 'get-ecommerce-categories', error);
    }
    this.usersService.getCurrentUser().subscribe({
      next: (user) => {
        this.user = user;
        this.setUserNameInitials();
        this.firstLoadFinished = true;
      },
      error: (error) => {
        this.errorReportingService.log('NavbarComponent.initialize()', 'get-current-user', error);
        this.firstLoadFinished = true;
      },
    });
    this.favoritesService.hasFavoriteItems().subscribe({
      next: (hasFavorites) => this.hasFavorites = hasFavorites,
      error: (error) => this.errorReportingService.log('NavbarComponent.initialize()', 'has-favorite-items', error),
    });
    this.searchHistoryService.getAll().subscribe({
      next: (searchHistory) => this.ecommerceSearchHistory = searchHistory,
      error: (error) => {
        this.errorReportingService.log('NavbarComponent.initialize()', 'get-search-history', error);
        this.ecommerceSearchHistory = null;
      },
    });
    this.shoppingCartsService.get().subscribe({
      next: (shoppingCart) => {
        if (this.isShoppingCartInitialized && shoppingCart && shoppingCart.totalItems !== this.shoppingCartDetail?.totalItems) {
          this.analyticsService.logShoppingCartUpdateEvent({
            currency: shoppingCart.currency,
            items: mapShoppingCartItemProductsToCartUpdateItems(shoppingCart.items),
            value: shoppingCart.total,
          });
        }
        this.shoppingCartDetail = shoppingCart;
        this.isShoppingCartInitialized = true;
      },
      error: (error) => {
        this.errorReportingService.log('NavbarComponent.initialize()', 'get-shopping-cart', error);
        this.shoppingCartDetail = null;
      },
    });
  }

  private setUserNameInitials(): void {
    this.userNameInitials = `${this.user?.name?.charAt(0).toLocaleUpperCase() ?? ''}${this.user?.fatherLastName?.charAt(0).toLocaleUpperCase() ?? ''}`;
  }
}
